import moment from 'moment';
import isArray from 'lodash/isArray';
import isNil from 'lodash/isNil';
import isString from 'lodash/isString';
import trim from 'lodash/trim';

import { APPROVAL_STATUS_APPROVED, APPROVAL_STATUS_REJECTED, getApprovalStatus, isInfluencerProfileRejected } from './profile';
import { convertStringToBoolean, convertStringToSnakeCase } from './helper/common';
import { HTML_DATE_FORMAT } from './date';

import type {
  BankDetails,
  BillingAddress,
  BrandProfile,
  BusinessDetails,
  InfluencerProfile,
  InfluencerTile,
  Organization,
  OrganizationProfileType,
  OrganizationSettings,
  OrganizationSettingsForm,
  OrganizationType,
  ProofDocuments
} from 'types';

export type PaymentDetailsCountryType = 'germany' | 'eu' | 'non_eu';
export type PaymentRequirementsCountryType = PaymentDetailsCountryType | 'italy' | 'no_country';

export const ORGANIZATION_TYPE_BRAND = 'brand';
export const ORGANIZATION_TYPE_BRAND_AGENCY = 'brand_agency';
export const ORGANIZATION_TYPE_INFLUENCER = 'influencer';
export const ORGANIZATION_TYPE_INFLUENCERS_AGENCY = 'influencers_agency';
export const ORGANIZATION_TYPE_CCM_ONLY = 'ccm_only';
export const ORGANIZATION_TYPE_CCM_MIXED = 'ccm_mixed';

export const ORGANIZATION_PROFILE_TYPE_BRAND = 'brand';
export const ORGANIZATION_PROFILE_TYPE_INFLUENCER = 'influencer';

export const COUNTRY_ITALY = 'italy';

export const EU_COUNTRIES = ['austria', 'belgium', 'bulgaria', 'croatia', 'cyprus', 'czechia', 'czech republic',
  'denmark', 'estonia', 'finland', 'france', 'greece', 'hungary', 'ireland', COUNTRY_ITALY,
  'latvia', 'lithuania', 'luxembourg', 'malta', 'netherlands', 'poland', 'portugal', 'romania',
  'slovakia', 'slovenia', 'spain', 'sweden', 'united_kingdom'];

const PAYMENT_DEFAULT_COUNTRY_GERMANY = 'germany';
const NO_COUNTRY = 'no_country';

const getCcmOrganizationStatus = (
  organization: Organization | null | undefined
): OrganizationType | null => {
  if (organization?.ccmStatus === 'only') {
    return ORGANIZATION_TYPE_CCM_ONLY;
  }

  if (organization?.ccmStatus === 'enabled') {
    return ORGANIZATION_TYPE_CCM_MIXED;
  }

  return null;
};

export function matchType(
  organization: Organization | undefined | null,
  types: Array<OrganizationType>
): boolean {
  const ccmOrganizationStatus = getCcmOrganizationStatus(organization);

  return Boolean(organization && types.includes(organization.type))
    || (
      Boolean(organization) &&
      Boolean(ccmOrganizationStatus) &&
      types.includes(ccmOrganizationStatus as OrganizationType)
    );
}

export function getOrganizationRealType(organization?: Organization | null): OrganizationType | undefined | null {
  return organization ? organization.type : null;
}

export function isAgencyFocusedOrganization(organization?: Organization | null): boolean {
  return matchType(organization, [ORGANIZATION_TYPE_BRAND_AGENCY, ORGANIZATION_TYPE_INFLUENCERS_AGENCY]);
}

export function isBrandsFocusedOrganization(organization?: Organization | null): boolean {
  return matchType(organization, [ORGANIZATION_TYPE_BRAND, ORGANIZATION_TYPE_BRAND_AGENCY]);
}

export function isCcmOnlyFocusedOrganization(organization?: Organization | null): boolean {
  return matchType(organization, [ORGANIZATION_TYPE_CCM_ONLY]);
}

export function isCcmMixedFocusedOrganization(organization?: Organization | null): boolean {
  return matchType(organization, [ORGANIZATION_TYPE_CCM_MIXED]);
}

export function isInfluencersFocusedOrganization(organization?: Organization | null): boolean {
  return matchType(organization, [ORGANIZATION_TYPE_INFLUENCER, ORGANIZATION_TYPE_INFLUENCERS_AGENCY]);
}

export function isInfluencersFocusedOrganizationRejected(organization?: Organization | null): boolean {
  return (
    isInfluencersFocusedOrganization(organization) &&
    Boolean(organization?.influencerProfiles) &&
    isInfluencerProfileRejected(organization?.influencerProfiles?.[0])
  );
}
export function isPlainBrandAgencyOrganization(organization?: Organization | null): boolean {
  return getOrganizationRealType(organization) === ORGANIZATION_TYPE_BRAND_AGENCY;
}

export function isPlainBrandOrganization(organization?: Organization | null): boolean {
  return getOrganizationRealType(organization) === ORGANIZATION_TYPE_BRAND;
}

export function isPlainInfluencerOrganization(organization?: Organization | null): boolean {
  return getOrganizationRealType(organization) === ORGANIZATION_TYPE_INFLUENCER;
}

export function getOrganizationProfileType(organization?: Organization | null): OrganizationProfileType {
  // @ts-ignore already checking for brand agency
  return isBrandsFocusedOrganization(organization) ?
    ORGANIZATION_PROFILE_TYPE_BRAND : (organization?.type || ORGANIZATION_PROFILE_TYPE_INFLUENCER);
}

export function organizationHasApprovedProfile(organization?: Organization | null): boolean {
  if (!organization) {
    return false;
  }

  const {
    influencerProfiles,
    brandProfiles,
    influencersAgencyProfile
  } = organization;
  const hasApprovalCreatorOrBrand = [...(influencerProfiles || []), ...(brandProfiles || [])]
    .map(getApprovalStatus)
    .includes(APPROVAL_STATUS_APPROVED);


  return hasApprovalCreatorOrBrand || !!influencersAgencyProfile;
}

export function organizationHasNotPendingProfile(organization?: Organization | null): boolean {
  if (!organization) {
    return false;
  }

  const { influencerProfiles, brandProfiles } = organization;
  const statuses = [...(influencerProfiles || []), ...(brandProfiles || [])].map(getApprovalStatus);
  return statuses.includes(APPROVAL_STATUS_APPROVED) || statuses.includes(APPROVAL_STATUS_REJECTED);
}

export function isConnectedToBrandAgencyOrganization(currentOrganization?: Organization | null): boolean {
  return Boolean(currentOrganization && currentOrganization.agencyOrganizationId && !currentOrganization?.influencerProfiles?.length);
}

export function getBrandProfileId(
  organization?: { brandProfiles?: Array<{ id: number }> } | null
): number | undefined | null {
  return organization
    && organization.brandProfiles
    && organization.brandProfiles[0]
    && organization.brandProfiles[0].id || null;
}

export function getAgencyProfileId(organization?: Organization | null): number | undefined | null {
  return organization
    && organization.influencersAgencyProfile
    && organization.influencersAgencyProfile?.id || null;
}

export function getBrandProfileName(organization?: Organization | null): string | undefined | null {
  return organization
    && organization.brandProfiles
    && organization.brandProfiles[0]
    && organization.brandProfiles[0].name || null;
}

export function getInfluencerProfileId(organization?: Organization | null): number | undefined | null {
  return organization
    && organization.influencerProfiles
    && organization.influencerProfiles[0]
    && organization.influencerProfiles[0].id || null;
}

export function getInfluencerProfile(
  organization?: Organization | null
): InfluencerProfile | null {
  if (isNil(organization)) {
    return null;
  }

  const allProfiles = organization.influencerProfiles;

  if (!isArray(allProfiles)) {
    return null;
  }

  return allProfiles[0];
}

export function hasAccessToBrandProfile(organizations: Array<Organization>, brandProfileId: number): boolean {
  return (organizations || []).some(organization => getBrandProfileId(organization) === brandProfileId);
}

export function getOrganizationProfileIfExist(organization: Organization | undefined | null): InfluencerTile | BrandProfile | undefined | null {
  let profile: any = null;

  if (isInfluencersFocusedOrganization(organization) && !isNil(organization?.influencerProfiles)) {
    profile = organization?.influencerProfiles.length === 1 ? organization.influencerProfiles[0] : null;
  } if (isInfluencersFocusedOrganization(organization) && !isNil(organization?.influencersAgencyProfile)) {
    profile = organization?.influencersAgencyProfile;
  } else if (isBrandsFocusedOrganization(organization) && !isNil(organization?.brandProfiles)) {
    profile = organization?.brandProfiles.length === 1 ? organization.brandProfiles[0] : null;
  }

  return profile;
}

function areOrganizationSettingsIncomplete(organizationDetails: OrganizationSettingsForm, requiredFields): boolean {
  const { bankDetails, businessDetails, billingAddress } = organizationDetails;

  const fields = {
    ...(bankDetails ?? {}),
    ...(businessDetails ?? {}),
    ...(billingAddress ?? {})
  };

  const requiredFieldMissing = requiredFields.filter((fieldName) => !fields[String(fieldName)]);

  return requiredFieldMissing.length > 0;
}

export function shouldInfluencerFillBankDetails(organizationSettings: OrganizationSettingsForm) {
  const requiredFields = [
    'accountHolder',
    'accountNumber',
    'swift'
  ];

  return areOrganizationSettingsIncomplete(organizationSettings, requiredFields);
}

export function shouldInfluencerFillBusinessDetails(organizationSettings: OrganizationSettingsForm) {
  const requiredFields = [
    'addressLine1',
    'city',
    'country',
    'postalCode'
  ];

  const registeredBusinessRequiredFields = [
    'companyName',
    'taxId'
  ];

  const isAddressIncomplete = areOrganizationSettingsIncomplete(organizationSettings, requiredFields);
  const isLegalIncomplete = areOrganizationSettingsIncomplete(organizationSettings, registeredBusinessRequiredFields);
  const isRegisteredBusiness = organizationSettings.businessDetails?.registeredBusiness;

  return isRegisteredBusiness
    ? isAddressIncomplete || isLegalIncomplete
    : isAddressIncomplete;
}

export function isInfluencerSettingsPageIncomplete(organizationSettings?: OrganizationSettingsForm | null) {
  if (!organizationSettings) {
    return true;
  }

  return shouldInfluencerFillBusinessDetails(organizationSettings)
    || shouldInfluencerFillBankDetails(organizationSettings);
}


export function isBrandSettingsPageInComplete(organizationSettings?: OrganizationSettingsForm | OrganizationSettings | null): boolean {
  if (!organizationSettings) {
    return true;
  }

  const requiredFields = [
    'website',
    'taxId',
    'legalName',
    'addressLine1',
    'city',
    'postalCode',
    'country'
  ];

  return areOrganizationSettingsIncomplete(organizationSettings, requiredFields);
}

export function prepareSettingsPayload(
  data: OrganizationSettingsForm
): OrganizationSettingsForm {
  const dataKeys = Object.keys(data);
  const accumulator: OrganizationSettingsForm = {
    bankDetails: {} as BankDetails,
    businessDetails: {} as BusinessDetails,
    billingAddress: {} as BillingAddress,
    proofDocuments: {} as ProofDocuments
  };

  const newData: OrganizationSettingsForm = dataKeys
    .reduce((acc, dataKey) => {
      const objKeys = Object.keys(data[dataKey]);

      objKeys.forEach(objKey => {
        const dataValue = data[dataKey][objKey];

        if (typeof dataValue === 'string') {
          acc[dataKey][objKey] = convertStringToBoolean(trim(dataValue));
        } else {
          acc[dataKey][objKey] = dataValue;
        }
      });

      return acc;
    }, accumulator);

  return newData;
}

export function filterAgencies<T extends Organization>(organizationsList: Array<T>): Array<T> {
  return organizationsList.filter(organization => organization.type !== ORGANIZATION_TYPE_BRAND_AGENCY);
}

export function getPaymentDetailsCountryType(country: string): PaymentDetailsCountryType {
  const formattedCountry = convertStringToSnakeCase(country);

  if (formattedCountry === PAYMENT_DEFAULT_COUNTRY_GERMANY || !country) {
    return 'germany';
  }

  if (EU_COUNTRIES.indexOf(formattedCountry) > -1) {
    return 'eu';
  }

  return 'non_eu';
}

export function getPaymentDetailsExplanationType(country?: string): PaymentRequirementsCountryType {
  if (country) {
    if (convertStringToSnakeCase(country) === COUNTRY_ITALY) {
      return COUNTRY_ITALY;
    }
    return getPaymentDetailsCountryType(country);
  }

  return NO_COUNTRY;
}

export const getIsFreemiumExpiredStatus = (
  freemiumUntil: string | null | undefined
): boolean => (
  isString(freemiumUntil) && moment().isAfter(moment(freemiumUntil || undefined), 'day')
);

export const getFreemiumDate = (
  date: string | null | undefined,
  type: 'actual' | 'db'
): string | null => {
  if (isNil(date)) {
    return null;
  }

  if (type === 'db') {
    return moment(date).add(1, 'd').format(HTML_DATE_FORMAT);
  }

  return moment(date).subtract(1, 'd').format(HTML_DATE_FORMAT);
};
